<template>
  <div class="file-tree-selector">
    <ProgressSpinnerContainer v-if="loading" />
    <Tree v-else 
      :value="fileDirectoriesTree" 
      v-model:expandedKeys="expandedKeys"
      v-model:selectionKeys="selectionKeys"
      :selectionMode="selectionMode ? 'multiple' : null"
      :metaKeySelection="false"
      @node-expand="onNodeExpand"
      @node-collapse="onNodeCollapse"
      :loading="loading"
      loadingMode="icon"
      class="file-directories-tree"
    >
      <template #default="slotProps">
        <span class="tree-node" :class="{ 'knowledge-source-node': slotProps.node.data?.type === 'knowledgeSource', 'uploaded-files-node': slotProps.node.data?.type === 'uploadedFiles' }">
          <span v-if="slotProps.node.data?.type === 'uploadedFiles'" class="uploaded-files-icon"><i class="pi pi-cloud-upload" /></span>
          <span v-else-if="slotProps.node.data?.type === 'knowledgeSource'" class="knowledge-source-icon">📖</span>
          <span v-else-if="slotProps.node.data?.type === 'site'" class="site-icon">💼</span>
          <span v-else-if="slotProps.node.data?.type === 'drive'" class="drive-icon">💾</span>
          <span v-else-if="!slotProps.node.leaf" class="folder-icon">📁</span>
          <i v-else :class="['pi', getFileIcon(slotProps.node.label), 'file-icon']"></i>
          {{ slotProps.node.label }}
          <i 
            v-if="showFileDetails && slotProps.node.leaf && slotProps.node.data?.type !== 'drive' && slotProps.node.data?.type !== 'site' && slotProps.node.data?.type !== 'knowledgeSource' && slotProps.node.data?.type !== 'uploadedFiles'" 
            class="pi pi-search search-icon" 
            @click.stop="onNodeClick(slotProps.node)"
            title="View file details"
          ></i>
        </span>
      </template>
    </Tree>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Tree from 'primevue/tree';
import ProgressSpinnerContainer from './ProgressSpinnerContainer.vue';
import { selectedAccount } from '../store/state.js';
import { apiGetAll } from '../util/api.js';

const props = defineProps({
  showFileDetails: {
    type: Boolean,
    default: false
  },
  selectionMode: {
    type: Boolean,
    default: false
  },
  selectionType: {
    type: String,
    default: 'both', // 'files', 'directories', 'both'
    validator: (value) => ['files', 'directories', 'both'].includes(value)
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  preloadFileIds: {
    type: Array,
    default: () => []
  },
  preloadDirectoryIds: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['node-click', 'update:modelValue', 'selection-change']);

const loading = ref(false);
const fileDirectories = ref([]);
const files = ref([]);
const expandedKeys = ref({});
const nodeLoadingStates = ref(new Map());
const selectionKeys = ref({});
const isUpdatingFromProp = ref(false);

function getFileIcon(filename) {
  if (!filename) return 'pi-file';
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf': return 'pi-file-pdf';
    case 'doc':
    case 'docm':
    case 'dotx':
    case 'docx': return 'pi-file-word';
    case 'xls':
    case 'xlsm':
    case 'xlt':
    case 'xltx':
    case 'xlsx': return 'pi-file-excel';
    case 'txt': return 'pi-file-edit';
    case 'jpg':
    case 'jpeg':
    case 'bmp':
    case 'tif':
    case 'tiff':
    case 'ico':
    case 'icon':
    case 'webp':
    case 'gif':
    case 'png': return 'pi-image';
    case 'm4a':
    case 'mp3': return 'pi-volume-up';
    case 'mov':
    case 'webm':
    case 'mp4': return 'pi-video';
    default: return 'pi-file';
  }
}

const fileDirectoriesTree = computed(() => {
  const buildTree = (parentId) => {
    const children = fileDirectories.value.filter(dir => dir.parent_file_directory_id === parentId);
    // Filter out files with null file_directories_id (they'll be in the "User Uploads" section)
    const childrenFiles = files.value.filter(file => file.file_directories_id === parentId && file.file_directories_id !== null);
    
    // Sort directories alphabetically by title
    const sortedChildren = children.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
    
    // Sort files alphabetically by filename
    const sortedChildrenFiles = childrenFiles.sort((a, b) => (a.filename ?? '').localeCompare(b.filename ?? ''));
    
    const directoryNodes = sortedChildren.map(dir => {
      // Determine the type based on metadata flags
      let type = undefined;
      if (dir.metadata?.isSiteDirectory) {
        type = 'site';
      } else if (dir.metadata?.isDriveDirectory) {
        type = 'drive';
      } else if (dir.metadata?.isKnowledgeSourceDirectory) {
        type = 'knowledgeSource';
      }
      
      // Determine if this directory is selectable based on selection type
      const selectable = !props.selectionMode || props.selectionType === 'directories' || props.selectionType === 'both';
      
      return {
        key: dir.id,
        label: dir.title || '',
        data: { ...dir, type },
        leaf: false,
        selectable: selectable,
        loading: nodeLoadingStates.value.get(dir.id) || false,
        children: buildTree(dir.id)
      };
    });
    
    const fileNodes = sortedChildrenFiles.map(file => {
      // Determine if this file is selectable based on selection type
      const selectable = !props.selectionMode || props.selectionType === 'files' || props.selectionType === 'both';
      
      return {
        key: file.id,
        label: file.filename || '',
        data: file,
        leaf: true,
        selectable: selectable,
        loading: false,
        children: []
      };
    });
    
    return directoryNodes.concat(fileNodes);
  };

  // Get root directories (those with no parent)
  const rootDirectories = fileDirectories.value.filter(dir => dir.parent_file_directory_id === null);
  
  // Sort root directories alphabetically by title
  const sortedRootDirectories = rootDirectories.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  
  // Build tree starting from root directories
  const rootNodes = sortedRootDirectories.map(dir => {
    // Determine the type based on metadata flags
    let type = undefined;
    if (dir.metadata?.isSiteDirectory) {
      type = 'site';
    } else if (dir.metadata?.isDriveDirectory) {
      type = 'drive';
    } else if (dir.metadata?.isKnowledgeSourceDirectory) {
      type = 'knowledgeSource';
    }
    
    // Determine if this directory is selectable based on selection type
    const selectable = !props.selectionMode || props.selectionType === 'directories' || props.selectionType === 'both';
    
    return {
      key: dir.id,
      label: dir.title || '',
      data: { ...dir, type },
      leaf: false,
      selectable: selectable,
      loading: nodeLoadingStates.value.get(dir.id) || false,
      children: buildTree(dir.id)
    };
  });
  
  // Add "User Uploads" fake directory if there are files with no directory
  // Only show this section when selection mode is enabled and selecting files
  const uploadedFiles = files.value.filter(file => file.file_directories_id === null);
  if (props.selectionMode && (props.selectionType === 'files' || props.selectionType === 'both') && uploadedFiles.length > 0) {
    const sortedUploadedFiles = uploadedFiles.sort((a, b) => {
      const nameA = a.filename || '';
      const nameB = b.filename || '';
      return nameA.localeCompare(nameB);
    });
    
    const uploadedFileNodes = sortedUploadedFiles.map(file => {
      // Files in this section are selectable if selection type allows files
      const selectable = !props.selectionMode || props.selectionType === 'files' || props.selectionType === 'both';
      
      return {
        key: file.id,
        label: file.filename || '',
        data: file,
        leaf: true,
        selectable: selectable,
        loading: false,
        children: []
      };
    });
    
    // Create fake "User Uploads" directory node
    const uploadedFilesNode = {
      key: 'uploaded-files',
      label: 'User Uploads',
      data: { type: 'uploadedFiles', isFakeDirectory: true },
      leaf: false,
      selectable: false, // Fake directory is never selectable
      loading: false,
      children: uploadedFileNodes
    };
    
    // Add the fake directory at the bottom of the root level
    rootNodes.push(uploadedFilesNode);
  }
  
  return rootNodes;
});

async function loadFileDirectories() {
  loading.value = true;
  try {
    // Load root directories
    const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"parent_file_directory_id":{"$null":null}}`;
    const rootDirectories = await apiGetAll(`/v1/file-directories${query}`);
    fileDirectories.value = rootDirectories;
    
    // Load additional files when in file select mode (files not in any directory)
    if (props.selectionMode && (props.selectionType === 'files' || props.selectionType === 'both')) {
      const uploadedFilesQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"file_directories_id":{"$null":null}}`;
      const uploadedFiles = await apiGetAll(`/v1/files${uploadedFilesQuery}`);
      files.value.push(...uploadedFiles);
    }
    
    // Preload selected items and their parent chains if needed
    const parentIdsToLoad = new Set();
    const expandKeys = {
      // Always expand the "User Uploads" fake directory if it exists
      'uploaded-files': true
    };
    
    // Load preselected files and get their parent directories
    if (props.preloadFileIds && props.preloadFileIds.length > 0) {
      try {
        const fileIdsStr = props.preloadFileIds.join(',');
        const filesQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${fileIdsStr}]}}`;
        const preloadedFiles = await apiGetAll(`/v1/files${filesQuery}`);
        
        // Add files that aren't already loaded (avoid duplicates with uploaded files)
        preloadedFiles.forEach(file => {
          if (!files.value.find(f => f.id === file.id)) {
            files.value.push(file);
          }
        });
        
        // Collect parent directory IDs
        preloadedFiles.forEach(file => {
          if (file.file_directories_id) {
            parentIdsToLoad.add(file.file_directories_id);
          }
        });
      } catch (error) {
        console.error('Error loading preselected files:', error);
      }
    }
    
    // Load preselected directories and get their parent directories
    if (props.preloadDirectoryIds && props.preloadDirectoryIds.length > 0) {
      try {
        const dirIdsStr = props.preloadDirectoryIds.join(',');
        const dirsQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${dirIdsStr}]}}`;
        const preloadedDirs = await apiGetAll(`/v1/file-directories${dirsQuery}`);
        
        // Collect parent directory IDs from preselected directories
        preloadedDirs.forEach(dir => {
          if (dir.parent_file_directory_id) {
            parentIdsToLoad.add(dir.parent_file_directory_id);
          }
        });
        
        // Add preloaded directories (they may not be in fileDirectories yet)
        preloadedDirs.forEach(dir => {
          if (!fileDirectories.value.find(d => d.id === dir.id)) {
            fileDirectories.value.push(dir);
          }
        });
      } catch (error) {
        console.error('Error loading preselected directories:', error);
      }
    }
    
    // Collect all directory IDs that will be expanded (for loading their files)
    const directoriesToLoadFiles = new Set();
    
    // Load parent chains recursively
    while (parentIdsToLoad.size > 0) {
      const currentParentIds = Array.from(parentIdsToLoad);
      parentIdsToLoad.clear();
      
      try {
        const parentIdsStr = currentParentIds.join(',');
        const parentsQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${parentIdsStr}]}}`;
        const parentDirs = await apiGetAll(`/v1/file-directories${parentsQuery}`);
        
        parentDirs.forEach(dir => {
          // Add to fileDirectories if not already there
          if (!fileDirectories.value.find(d => d.id === dir.id)) {
            fileDirectories.value.push(dir);
          }
          
          // Mark this directory as expanded
          expandKeys[dir.id] = true;
          
          // Mark for file loading
          directoriesToLoadFiles.add(dir.id);
          
          // Add its parent to the next batch if it exists
          if (dir.parent_file_directory_id) {
            parentIdsToLoad.add(dir.parent_file_directory_id);
          }
        });
      } catch (error) {
        console.error('Error loading parent directories:', error);
        break;
      }
    }
    
    // Load files and child directories for all expanded directories
    if (directoriesToLoadFiles.size > 0) {
      try {
        const dirIdsForFiles = Array.from(directoriesToLoadFiles).join(',');
        
        // Load files in these directories
        const filesInExpandedDirsQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"file_directories_id":{"$in":[${dirIdsForFiles}]}}`;
        const filesInExpandedDirs = await apiGetAll(`/v1/files${filesInExpandedDirsQuery}`);
        
        // Add files that aren't already loaded
        filesInExpandedDirs.forEach(file => {
          if (!files.value.find(f => f.id === file.id)) {
            files.value.push(file);
          }
        });
        
        // Load child directories in these directories
        const childDirsInExpandedDirsQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"parent_file_directory_id":{"$in":[${dirIdsForFiles}]}}`;
        const childDirsInExpandedDirs = await apiGetAll(`/v1/file-directories${childDirsInExpandedDirsQuery}`);
        
        // Add child directories that aren't already loaded
        childDirsInExpandedDirs.forEach(dir => {
          if (!fileDirectories.value.find(d => d.id === dir.id)) {
            fileDirectories.value.push(dir);
          }
        });
      } catch (error) {
        console.error('Error loading files/directories in expanded directories:', error);
      }
    }
    
    // Set expanded keys for the loaded parent chain
    expandedKeys.value = expandKeys;
    
    // Merge selection keys for preloaded items (don't overwrite existing selections)
    if ((props.preloadFileIds && props.preloadFileIds.length > 0) || 
        (props.preloadDirectoryIds && props.preloadDirectoryIds.length > 0)) {
      isUpdatingFromProp.value = true;
      
      const updatedSelectionKeys = { ...selectionKeys.value };
      
      if (props.preloadFileIds && props.preloadFileIds.length > 0) {
        props.preloadFileIds.forEach(id => {
          updatedSelectionKeys[id] = true;
        });
      }
      if (props.preloadDirectoryIds && props.preloadDirectoryIds.length > 0) {
        props.preloadDirectoryIds.forEach(id => {
          updatedSelectionKeys[id] = true;
        });
      }
      
      selectionKeys.value = updatedSelectionKeys;
      
      setTimeout(() => {
        isUpdatingFromProp.value = false;
      }, 0);
    }
    
  } catch (error) {
    console.error('Error loading file directories:', error);
  } finally {
    loading.value = false;
  }
}

async function onNodeExpand(node) {
  // Set the expanded state for this node
  expandedKeys.value[node.key] = true;

  // Skip loading for fake directories (they have pre-populated children)
  if (node.data?.isFakeDirectory) {
    return;
  }

  // Check if this node already has children loaded
  if (node.children && node.children.length > 0) {
    return;
  }
  
  // Set loading state for this specific node
  nodeLoadingStates.value.set(node.key, true);
  
  try {
    const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"parent_file_directory_id":{"$eq":${node.key}}}`;
    const childDirectories = await apiGetAll(`/v1/file-directories${query}`);
    const filesQuery = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"file_directories_id":{"$eq":${node.key}}}`;
    const childFiles = await apiGetAll(`/v1/files${filesQuery}`);
    
    // Add the child directories to the main fileDirectories array (avoid duplicates)
    childDirectories.forEach(dir => {
      if (!fileDirectories.value.find(d => d.id === dir.id)) {
        fileDirectories.value.push(dir);
      }
    });
    
    // Add the child files (avoid duplicates)
    childFiles.forEach(file => {
      if (!files.value.find(f => f.id === file.id)) {
        files.value.push(file);
      }
    });

  } catch (error) {
    console.error('Error loading children for node:', node.label, error);
  } finally {
    nodeLoadingStates.value.set(node.key, false);
  }
}

function onNodeCollapse(node) {
  expandedKeys.value[node.key] = false;
}

function onNodeClick(node) {
  emit('node-click', node);
}

// Get all nodes (files and directories) in a flat structure
function getAllNodes() {
  const nodes = [];
  
  const traverse = (nodeList) => {
    nodeList.forEach(node => {
      nodes.push(node);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  };
  
  traverse(fileDirectoriesTree.value);
  return nodes;
}

// Get selected items based on selection type
const selectedItems = computed(() => {
  const allNodes = getAllNodes();
  const selected = [];
  
  Object.keys(selectionKeys.value).forEach(key => {
    const selectionValue = selectionKeys.value[key];
    
    // In "multiple" mode, selected items are simply truthy values
    if (selectionValue) {
      const node = allNodes.find(n => String(n.key) === String(key));
      if (node) {
        // Skip fake directories (defensive check)
        if (node.data?.isFakeDirectory) {
          return;
        }
        
        const isFile = node.leaf;
        const isDirectory = !node.leaf;
        
        // Filter based on selection type
        if (props.selectionType === 'files' && isFile) {
          selected.push({ id: node.key, ...node.data });
        } else if (props.selectionType === 'directories' && isDirectory) {
          selected.push({ id: node.key, ...node.data });
        } else if (props.selectionType === 'both') {
          selected.push({ id: node.key, ...node.data, isFile, isDirectory });
        }
      }
    }
  });
  
  return selected;
});

// Watch for selection changes
watch(selectedItems, (newSelection) => {
  if (isUpdatingFromProp.value) return;
  
  const ids = newSelection.map(item => item.id);
  emit('update:modelValue', ids);
  emit('selection-change', newSelection);
}, { deep: true });

// Initialize selection from modelValue
watch(() => props.modelValue, (newValue) => {
  isUpdatingFromProp.value = true;
  
  if (newValue && newValue.length > 0) {
    const newSelectionKeys = {};
    newValue.forEach(id => {
      newSelectionKeys[id] = true;
    });
    selectionKeys.value = newSelectionKeys;
  } else {
    selectionKeys.value = {};
  }
  
  // Use nextTick to ensure the selection update completes before resetting the flag
  setTimeout(() => {
    isUpdatingFromProp.value = false;
  }, 0);
}, { immediate: true });

onMounted(() => {
  loadFileDirectories();
});
</script>

<style scoped>
.file-tree-selector {
  width: 100%;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.knowledge-source-icon,
.site-icon,
.drive-icon,
.folder-icon,
.uploaded-files-icon {
  font-size: 1.2rem;
}

.file-icon {
  font-size: 1rem;
  color: #6c757d;
}

.search-icon {
  margin-left: auto;
  cursor: pointer;
  color: #007bff;
  font-size: 0.9rem;
}

.search-icon:hover {
  color: #0056b3;
}

.knowledge-source-node {
  font-weight: 600;
  color: #0d6efd;
}

.uploaded-files-node {
  font-weight: 600;
  color: #6c757d;
  font-style: italic;
}

/* Style for selected nodes in multiple selection mode */
.file-directories-tree :deep(.p-treenode-content.p-highlight) {
  background-color: #e3f2fd !important;
  border: 1px solid #2196f3;
}

.file-directories-tree :deep(.p-treenode-content.p-highlight:hover) {
  background-color: #bbdefb !important;
}

.file-directories-tree :deep(.p-treenode-content) {
  cursor: pointer;
}
</style>

